import React, { useState, useEffect } from 'react';
import { formatRemainingTime, getRemainingSessionTime, resetSessionTimeout } from '../../utils/sessionManager';

interface SessionTimeoutWarningProps {
  isOpen: boolean;
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  isOpen,
  onStayLoggedIn,
  onLogout,
}) => {
  const [remainingTime, setRemainingTime] = useState<string>('2:00');

  useEffect(() => {
    if (!isOpen) return;

    // Update remaining time every second
    const interval = setInterval(() => {
      const ms = getRemainingSessionTime();
      setRemainingTime(formatRemainingTime(ms));

      // Auto-logout if time runs out
      if (ms <= 0) {
        clearInterval(interval);
        onLogout();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, onLogout]);

  const handleStayLoggedIn = () => {
    resetSessionTimeout();
    onStayLoggedIn();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">⚠️</span>
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-bold text-gray-900">Session Timeout Warning</h2>
              <p className="text-sm text-gray-600">Your session is about to expire</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              For your security, your session will automatically expire due to inactivity.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-yellow-800 mb-2">Time remaining:</p>
                  <p className="text-4xl font-bold text-yellow-900">{remainingTime}</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Click "Stay Logged In" to continue your session, or "Logout" to end your session now.
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onLogout}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Logout
            </button>
            <button
              onClick={handleStayLoggedIn}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
